import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useState } from "react";
import {
  CrosshairIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";

const currentPosition = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error(translateLabel("Your browser does not support location.")));
    return;
  }
  navigator.geolocation.getCurrentPosition(resolve, reject, {
    enableHighAccuracy: false,
    timeout: 10000,
    maximumAge: 300000,
  });
});

export function HomeLocationCard({ value, onChange }) {
  const [query, setQuery] = useState(value?.name || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setQuery(value?.name || "");
  }, [value?.name]);

  const saveLocation = async (location) => {
    const { data } = await api.post("/rider-profile", { home_location: location });
    onChange?.(data?.home_location || location);
    setQuery(data?.home_location?.name || location.name);
    toast.success(translateLabel("Home location saved"));
  };

  const findAndSave = async () => {
    const text = query.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.get("/geocode", { params: { q: text } });
      const first = data?.results?.[0];
      if (!first) {
        toast.error(translateLabel("No matching place found"));
        return;
      }
      await saveLocation({ name: first.name, lat: first.lat, lng: first.lng });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || translateLabel("Could not save home location"));
    } finally {
      setBusy(false);
    }
  };

  const useCurrentLocation = async () => {
    setBusy(true);
    try {
      const position = await currentPosition();
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      let name = translateLabel("Current location");
      try {
        const { data } = await api.get("/reverse-geocode", { params: { lat, lng } });
        if (data?.name) name = data.name;
      } catch {
        // Coordinates are still valid if reverse geocoding is unavailable.
      }
      await saveLocation({ name, lat, lng });
    } catch (error) {
      const message = error?.code === 1
        ? translateLabel("Location permission was denied")
        : (error.message || translateLabel("Could not get your location"));
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const clearLocation = async () => {
    setBusy(true);
    try {
      await api.post("/rider-profile", { home_location: null });
      onChange?.(null);
      setQuery("");
      toast.success(translateLabel("Home location cleared"));
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || translateLabel("Could not clear home location"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-w-0 rounded-2xl border border-white/10 bg-[linear-gradient(180deg,#151517,#111113)] p-4 shadow-[0_18px_50px_rgba(0,0,0,0.12)] md:rounded-3xl md:p-6">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#CCFF00]/10 bg-[#CCFF00]/[0.06] text-[#CCFF00]">
          <MapPinIcon size={20} weight="bold" />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#CCFF00]">{translateLabel("Default origin")}</div>
          <h2 className="mt-1 font-display text-2xl font-bold uppercase text-white">{translateLabel("Home location")}</h2>
          <p className="mt-1 text-sm leading-5 text-zinc-400">
            {translateLabel("Used by Discovery and Compare when you have not chosen another starting point.")}
          </p>
        </div>
      </div>

      {value && (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-[#CCFF00]/15 bg-[#CCFF00]/[0.05] px-4 py-3">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel("Saved location")}</div>
            <div className="mt-1 truncate font-bold text-white">{value.name}</div>
          </div>
          <button
            type="button"
            onClick={clearLocation}
            disabled={busy}
            aria-label={translateLabel("Clear home location")}
            className="shrink-0 rounded-xl p-2 text-zinc-500 transition-colors hover:bg-white/5 hover:text-red-300 disabled:opacity-50"
          >
            <TrashIcon size={17} />
          </button>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <MagnifyingGlassIcon
            size={17}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                findAndSave();
              }
            }}
            placeholder={translateLabel("City or postcode")}
            className="w-full rounded-xl border border-white/10 bg-black/20 py-3 pl-10 pr-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#CCFF00]/40"
          />
        </div>
        <button
          type="button"
          onClick={findAndSave}
          disabled={busy || !query.trim()}
          className="rounded-xl bg-[#CCFF00] px-5 py-3 text-sm font-extrabold text-black disabled:cursor-not-allowed disabled:opacity-45"
        >
          {busy ? translateLabel("Saving…") : translateLabel("Set location")}
        </button>
      </div>

      <button
        type="button"
        onClick={useCurrentLocation}
        disabled={busy}
        className="mt-2 inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/[0.04] hover:text-white disabled:opacity-45"
      >
        <CrosshairIcon size={17} weight="bold" />
        {translateLabel("Use current location")}
      </button>

      <p className="mt-3 text-xs leading-5 text-zinc-600">
        {translateLabel("BPF only saves a location when you choose one here. Browser location is not stored automatically.")}
      </p>
    </section>
  );
}
